/*
 * Detalle de la orden.
 *
 * Esta formada por componentes encargados de cargar la información
 * básica de la orden, datos del cliente, dirección de envío y facturación.
 * Carga la lista de productos y circuitos así como el hisotial de pagos.
 *
 * Created on Fri Mar 31 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <validation-observer ref="orderForm">

      <!-- #region::Skeleton zone -->
      <template v-if="loadingOrder">
        <SaveOrderFormSkeleton />
      </template>
      <!-- #endregion::Skeleton zone -->

      <template v-else>
        <div class="mb-2">

          <!-- #region::Order options -->
          <OrderDetailsOptions
            class="mb-1"
            :callback="onUpdateOrder"
            :can-manage-orders="canManageOrders"
            @reload-order="loadOrderData"
          />
          <!-- #endregion::Order options -->

          <!-- #region::Customer info and basic order info -->
          <OrderCustomerData />
          <OrderExtraData @reload-order="loadOrderData" />
          <!-- #endregion::Customer info and basic order info -->

          <!-- #region::Products and circuits lists -->
          <OrderProductsDetails />
          <OrderCircuitsDetails class="mt-2" />
          <!-- #endregion::Products and circuits lists -->

          <!-- #region::Invoice order info and payments history list -->
          <OrderTotalDetails
            v-if="canShowPaymentHistory"
            @reload-order="loadOrderData"
          />
          <OrderPaymentsHistory
            v-if="!loadingOrder && canShowPaymentHistory"
            :can-manage-orders="canManageOrders"
            @reload-order="loadOrderData"
          />
          <OrderComments
            v-if="canShowPaymentHistory"
            :key="reloadKey"
            :order-id="id"
          />
          <!-- #endregion::Invoice order info and payments history list -->
        </div>
      </template>

    </validation-observer>
  </div>
</template>

<script>
// #region Imports
import { mapActions, mapGetters } from 'vuex'

import { ValidationObserver, localize } from 'vee-validate'

import OrderExtraData from '@/modules/trade/orders/components/OrderExtraData.vue'
import OrderCustomerData from '@/modules/trade/orders/components/OrderCustomerData.vue'
import OrderTotalDetails from '@/modules/trade/orders/components/OrderTotalDetails.vue'
import OrderDetailsOptions from '@/modules/trade/orders/components/OrderDetailsOptions.vue'
import OrderPaymentsHistory from '@/modules/trade/orders/components/OrderPaymentsHistory.vue'
import SaveOrderFormSkeleton from '@/modules/trade/orders/components/SaveOrderFormSkeleton.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getError from '@/helpers/ErrorsHandler'
import { adminLevel, ADMIN_USER_LEVELS } from '@/helpers/UserLevel'
import OrderComments from '../components/OrderComments.vue'
// #endregion

export default {
  components: {
    OrderComments,
    OrderExtraData,
    OrderTotalDetails,
    OrderCustomerData,
    ValidationObserver,
    OrderDetailsOptions,
    OrderPaymentsHistory,
    SaveOrderFormSkeleton,
    OrderProductsDetails: () => import('@/modules/trade/orders/components/OrderProductsDetails.vue'),
    OrderCircuitsDetails: () => import('@/modules/trade/orders/components/OrderCircuitsDetails.vue'),
  },
  props: {
    /**
     * Identificador de la orden
     */
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loadingOrder: false,
      /**
       * Información del usuario loggeado
       */
      userData: JSON.parse(localStorage.getItem('userData')),
      reloadKey: 0,
      adminLevel,
      ADMIN_USER_LEVELS,
    }
  },
  computed: {
    ...mapGetters({
      getIVA: 'orders/getIVA',
      getOrder: 'orders/getOrder',
      getTotalMXN: 'orders/getTotalMXN',
      getDetailProducts: 'orders/getDetailProducts',
      getDetailCircuits: 'orders/getDetailCircuits',
      getCountToInvoice: 'orders/getCountToInvoice',
      getInvoiceRequired: 'orders/getInvoiceRequired',
      getEditionModeState: 'orders/getEditionModeState',
      getDetailProductsPieces: 'orders/getDetailProductsPieces',

      /**
       * Lista de circuitos con precio de oferta seleccionado
       */
      getCircuitsWithOfferPriceSelected: 'orders/getCircuitsWithOfferPriceSelected',
    }),
    /**
     * IVA calculado en tiempo real
     */
    IVA: {
      get() { return this.getIVA },
    },
    /**
     * Información de la orden
     */
    order: {
      get() { return this.getOrder },
    },
    /**
     * Total de la orden en MXN calculado en tiempo real
     */
    totalMXN: {
      get() { return this.getTotalMXN },
    },
    /**
     * Estado actual de la orden
     */
    status: {
      get() { return this.order.order.current_status },
    },
    /**
     * Lista de productos de la orden
     */
    detailProducts: {
      get() { return this.getDetailProducts },
    },
    /**
     * Lista de circuitos de la orden
     */
    detailCircuits: {
      get() { return this.getDetailCircuits },
    },
    /**
     * Monto a facturar
     */
    countToInvoice: {
      get() { return this.getCountToInvoice },
    },
    /**
     * Indica si la orden requiere factura
     */
    invoiceRequired: {
      get() { return this.getInvoiceRequired },
    },
    editionModeState: {
      get() { return this.getEditionModeState },
      set(value) { this.setEditionModeState(value) },
    },
    /**
     * Lista de piezas de los productos de la orden
     */
    detailProductsPieces: {
      get() { return this.getDetailProductsPieces },
    },
    /**
     * Lista de circuitos con precio de oferta seleccionado
     */
    circuitsWithOfferPriceSelected: {
      get() { return this.getCircuitsWithOfferPriceSelected },
    },
    canShowPaymentHistory() {
      return this.userData.Role !== 'Coordinador de Almacen'
    },
    canManageOrders() {
      return this.adminLevel() === this.ADMIN_USER_LEVELS.ADMINISTRADOR
    },
  },

  /**
  * Hook de creación de componente
  *
  * @summary Llama a la carga del registro de orden
  * @exception Retorna a la lista de órdenes.
  */
  async created() {
    try {
      this.loadingOrder = true
      await this.loadOrderData()
      if (this.$ability.can('request', 'Devolution')) {
        await this.checkRefundStatus(this.order.order.ID)
      }

      localize('es')
    } catch (error) {
      this.showToast('Error de validación', getError(error), 'danger')
      this.$router.push({ name: 'orders-home' })
    } finally {
      this.$swal.close()
      this.loadingOrder = false
    }
  },

  /**
  * Hook de la destrucción del componente
  *
  * @summary Limpia la información del componente cargada en el state management
  */
  beforeDestroy() {
    this.clearData()
  },
  methods: {
    ...mapActions({
      clearData: 'orders/clearData',
      loadOrder: 'orders/loadOrder',
      updateOrder: 'orders/updateOrder',
      loadPayments: 'quotes/loadPayments',
      checkRefundStatus: 'orders/checkRefundStatus',
      setEditionModeState: 'orders/setEditionModeState',
      setOriginProductRoute: 'products/setOriginProductRoute',
    }),

    /**
    * Carga la información de la orden
    *
    * @summary Establece la información de la orden a partir del identificador
    * y establece la ruta para los productos
    * @exception Si el producto no existe redirige a la página de no content.
    */
    async loadOrderData() {
      try {
        this.reloadKey += 1
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()
        await this.loadOrder(this.id)

        this.setOriginProductRoute({
          name: this.$route.name,
          params: {
            id: this.id,
          },
        })
        this.$swal.close()
      } catch (error) {
        if (error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      } finally {
        this.editionModeState = false
      }
    },

    /**
    * Actualiza una orden
    *
    * @summary Actualiza la información de la orden contemplando productos y circuitos
    * @exception Se muestra cuando existe un error de actualización en el server
    */
    async onUpdateOrder() {
      const success = await this.$refs.orderForm.validate()

      if (!this.circuitsWithDeliveryMethod()) {
        this.showToast(
          'Error de validación',
          'Uno o más circuitos cuentan con productos sin método de envío especificado.',
          'danger',
        )

        return
      }

      // Verifica que todos los productos de los circuitos cuenten con precio unitario
      if (!this.circuitsWithTotal()) {
        this.showToast(
          'Error de validación',
          'Uno o más circuitos cuentan con productos sin precio unitario especificado.',
          'danger',
        )
        return
      }

      // Verifica si la orden se encuentra en suministro y si todos los productos y circuitos se encuentran en estado entregado
      if (this.status === 'En suministro' && this.allPiecesWithDeliveredState() && this.allCircuitsWithDeliveredState()) {
        const continueUpdating = await this.canContinueUpdatingOrder()

        if (!continueUpdating) {
          return
        }
      }

      if (success) {
        this.$swal({
          title: 'Espere por favor',
          allowOutsideClick: false,
        })

        this.$swal.showLoading()

        try {
          const formData = new FormData()

          this.detailProducts.forEach(orderProduct => {
            formData.append('circuits_products[]', this.productData(orderProduct))
          })

          this.detailCircuits.forEach(orderCircuit => {
            orderCircuit.products.forEach(product => {
              formData.append('circuits_products[]', this.productData(product, orderCircuit.IdCircuit, orderCircuit.uuid))
            })
          })

          formData.append('iva', this.IVA.toFixed(2))
          formData.append('invoice', this.invoiceRequired)
          formData.append('amountInvoiced', this.invoiceRequired === 'Sí' ? this.countToInvoice : 0)

          // eslint-disable-next-line no-restricted-syntax
          for (const pair of formData.entries()) {
            console.log(`${pair[0]}, ${pair[1]}`)
          }

          await this.updateOrder({ id: this.id, formData })

          this.showToast(
            'Orden actualizada',
            'La orden ha sido actualizada correctamente.',
            'success',
          )
          await this.loadOrderData()
        } catch (error) {
          this.$swal.close()
          this.showToast('Error de validación', getError(error), 'danger')
          this.loadOrderData()
        } finally {
          this.editionModeState = false
        }
      } else {
        this.showToast(
          'Error de validación',
          'Se encontraron uno o más errores en la validación del formulario de la orden.',
          'danger',
        )
      }
    },
    productData(product, idCircuit = '') {
      const pieces = []

      product.pieces.forEach(piece => {
        pieces.push({ idPiece: piece.IdPiece })
      })

      const offertedCircuit = this.circuitsWithOfferPriceSelected.find(c => c.IdCircuit === idCircuit)

      let selectedUnitPrice = ''
      let selectedUnitPriceOrigin = ''
      let selectedUnitPriceType = ''

      if (idCircuit && !offertedCircuit) {
        selectedUnitPrice = product.unitPrice
        selectedUnitPriceOrigin = product.unitPriceOrigin
        selectedUnitPriceType = product.unitPriceType
      } else if (!idCircuit && !offertedCircuit) {
        selectedUnitPrice = product.unitPrice || product.UnitPrice
        selectedUnitPriceOrigin = product.unitPriceOrigin || product.UnitPriceOrigin
        selectedUnitPriceType = product.unitPriceType || product.UnitPriceType
      }

      const productData = JSON.stringify({
        idCircuit,
        idProduct: product.IdProduct,
        unitPrice: selectedUnitPrice,
        unitPriceOrigin: selectedUnitPriceOrigin,
        unitPriceType: selectedUnitPriceType,
        offerPrice: offertedCircuit?.OfferPrice || '',
        methodDelivery: product.DeliveredMethod,
        pieces,
      })

      return productData
    },

    /**
     * Verificación del método de entrega
     *
     * @summary Verifica si se ha establecido un método de entrega para todos los productos de los circuitos
     * @return {boolean} Retorna true si todos los productos de los circuitos tienen un método de entrega
     */
    circuitsWithDeliveryMethod() {
      let withDeliveryMethod = true

      this.detailCircuits.forEach(orderCircuit => {
        const hasNotDeliveryMethod = product => product.DeliveredMethod === null || product.DeliveredMethod === ''
        const notDeliveryMethod = orderCircuit.products.some(hasNotDeliveryMethod)

        if (notDeliveryMethod) {
          withDeliveryMethod = false
        }
      })

      return withDeliveryMethod
    },

    /**
    * Verifiación del total de los circuitos
    *
    * @summary Verifica si se establecido a todos los productos de los circuitos un precio unitario
    * @return {boolean} Retorna true si todos los productos de los circuitos tienen un precio unitario
    */
    circuitsWithTotal() {
      let withTotal = true

      // Se recorren todos los circuitos de la cotización
      this.detailCircuits.forEach(orderCircuit => {
        // Se verifica si el circuito tiene precio de oferta seleccionado
        const offertedCircuit = this.circuitsWithOfferPriceSelected.find(c => c.IdCircuit === orderCircuit.IdCircuit)

        // Se omite la verificación de precio unitario si el circuito tiene precio de oferta seleccionado
        if (!offertedCircuit) {
          const hasNotUnitPriceDefined = product => product.unitPrice === 0
          const notUnitPriceDefined = orderCircuit.products.some(hasNotUnitPriceDefined)

          if (notUnitPriceDefined) {
            withTotal = false
          }
        }
      })

      return withTotal
    },

    /**
     * Verificación	del estado de entrega de las piezas
     *
     * @summary Verifica si todas las piezas de los productos de la orden tienen el estado de entregado
     * (Delivered:"Entregado") de la lista: detailProductsPieces
     * @return {boolean} Retorna true si todas las piezas de los productos de la orden tienen el estado de entregado
     */
    allPiecesWithDeliveredState() {
      let allPiecesDelivered = true

      this.detailProductsPieces.forEach(piece => {
        if (piece.Delivered !== 'Entregado') {
          allPiecesDelivered = false
        }
      })

      return allPiecesDelivered
    },

    /**
     * Verificación del estado de entrega de los circuitos
     *
     * @summary Verifica si todos los circuitos de la orden tienen el estado de entregado
     * (Delivered:"Entregado") de la lista: detailCircuits
     * @return {boolean} Retorna true si todos los circuitos de la orden tienen el estado de entregado
     */
    allCircuitsWithDeliveredState() {
      let allCircuitsDelivered = true

      this.detailCircuits.forEach(circuit => {
        if (circuit.Delivered !== 'Entregado') {
          allCircuitsDelivered = false
        }
      })

      return allCircuitsDelivered
    },

    /**
     * Continuar con la actualización de la orden
     * @summary Muestra un mensaje de confirmación para continuar con la actualización de la orden
     * @return {boolean} Retorna true si se confirma la actualización de la orden
     */
    async canContinueUpdatingOrder() {
      const result = await this.$swal({
        title: '¿Guardar cambios?',
        text: '¡La orden pasará a estatus Completada porque ya no tiene productos por entregar!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Aceptar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      return result.isConfirmed
    },

    /**
    * Mensaje de alerta
    *
    * @summary Muestra un mensaje de alerta personalizado
    * @param {string} title - Título del mensaje
    * @param {string} text - Texto del mensaje
    * @param {string} variant - Tipo de mensaje
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
